import { useTranslation } from 'react-i18next'
import { EID } from '@/lib/element-ids.registry'
import { Select } from '@/components/v2/ui/Select'
import { Checkbox } from '@/components/v2/ui/Checkbox'
import type { Metric, CompareMode } from '@/lib/products/salesSeriesTransforms'

interface Channel { id: string; name: string }
export interface PerformanceFiltersProps {
  metric: Metric; granularity: 'daily' | 'weekly'; rangeDays: 7 | 30 | 90
  compareMode: CompareMode; channelId: string | null
  types: ('image' | 'text')[]; channels: Channel[]
  onMetricChange: (m: Metric) => void; onGranularityChange: (g: 'daily' | 'weekly') => void
  onRangeChange: (d: 7 | 30 | 90) => void; onCompareChange: (c: CompareMode) => void
  onChannelChange: (id: string | null) => void; onTypesChange: (t: ('image' | 'text')[]) => void
}

function ToggleBtn({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button type="button" aria-pressed={active} onClick={onClick}
      className={`px-3 py-1.5 text-sm rounded transition-colors ${active ? 'bg-primary-600 text-white' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300'}`}>
      {label}
    </button>
  )
}

export function PerformanceFilters({
  metric, granularity, rangeDays, compareMode, channelId,
  types, channels, onMetricChange, onGranularityChange, onRangeChange,
  onCompareChange, onChannelChange, onTypesChange,
}: PerformanceFiltersProps) {
  const { t } = useTranslation()
  const rangeOptions = [
    { value: '7', label: t('performance.range.7d') },
    { value: '30', label: t('performance.range.30d') },
    { value: '90', label: t('performance.range.90d') },
  ]
  const compareOptions = [
    { value: 'previous', label: t('performance.compare.previous') },
    { value: 'year', label: t('performance.compare.year') },
    { value: 'none', label: t('performance.compare.none') },
  ]
  const channelOptions = [
    { value: '__all__', label: t('performance.channel.all') },
    ...channels.map((c) => ({ value: c.id, label: c.name })),
  ]
  function toggleType(type: 'image' | 'text') {
    const next = types.includes(type) ? types.filter((v) => v !== type) : [...types, type]
    onTypesChange(next)
  }
  return (
    <div className="flex flex-wrap gap-4 items-end p-3 bg-neutral-50 dark:bg-neutral-900 rounded-lg">
      <div className="flex gap-1" id={EID['performance-metric-toggle']}>
        <ToggleBtn active={metric === 'revenue'} onClick={() => onMetricChange('revenue')} label={t('performance.metric.revenue')} />
        <ToggleBtn active={metric === 'quantity'} onClick={() => onMetricChange('quantity')} label={t('performance.metric.quantity')} />
      </div>
      <div className="flex gap-1" id={EID['performance-granularity-toggle']}>
        <ToggleBtn active={granularity === 'daily'} onClick={() => onGranularityChange('daily')} label={t('performance.granularity.daily')} />
        <ToggleBtn active={granularity === 'weekly'} onClick={() => onGranularityChange('weekly')} label={t('performance.granularity.weekly')} />
      </div>
      <div className="w-32">
        <Select id={EID['performance-range-select']} options={rangeOptions} value={String(rangeDays)}
          onValueChange={(v) => onRangeChange(Number(v) as 7 | 30 | 90)}
          data-testid="range-select" aria-label={t('performance.range.30d')} />
      </div>
      <div className="w-40">
        <Select id={EID['performance-compare-select']} options={compareOptions} value={compareMode}
          onValueChange={(v) => onCompareChange(v as CompareMode)}
          data-testid="compare-select" aria-label={t('performance.compare.previous')} />
      </div>
      <div className="w-40">
        <Select id={EID['performance-channel-select']} options={channelOptions}
          value={channelId ?? '__all__'}
          onValueChange={(v) => onChannelChange(v === '__all__' ? null : v)}
          data-testid="channel-select" aria-label={t('performance.channel.all')} />
      </div>
      <div className="flex gap-3">
        <Checkbox label={t('performance.type.image')} checked={types.includes('image')} onChange={() => toggleType('image')} />
        <Checkbox label={t('performance.type.text')} checked={types.includes('text')} onChange={() => toggleType('text')} />
      </div>
    </div>
  )
}
